import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid2';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Typography,
    Chip,
    Card,
    CardContent,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import FolderIcon from '@mui/icons-material/Folder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { formatDate } from '../../../utils';
import { formatFileSize } from '../services/closeoutDocsService';
import { getFileIcon } from '../utils/fileIconUtils';

/**
 * UnifiedList Component
 * Displays folders and documents in a single unified table/list
 * Folders appear first, followed by documents
 *
 * @param {Array} folders - Array of folder objects
 * @param {Array} documents - Array of document objects
 * @param {string} sortBy - Current sort field ('name' or 'date')
 * @param {string} sortOrder - Current sort order ('asc' or 'desc')
 * @param {Function} onSortChange - Callback when sort changes
 * @param {Function} onFolderClick - Callback when a folder is clicked
 * @param {Function} onDocumentClick - Callback when a document is clicked
 * @param {Function} onFolderRename - Callback for folder rename
 * @param {Function} onFolderDelete - Callback for folder delete
 * @param {Function} onFolderMove - Callback for folder move
 * @param {Function} onDocumentRename - Callback for document rename
 * @param {Function} onDocumentDownload - Callback for document download
 * @param {Function} onDocumentDelete - Callback for document delete
 * @param {Function} onDocumentMove - Callback for document move
 */
const UnifiedList = ({
    folders = [],
    documents = [],
    sortBy = 'name',
    sortOrder = 'asc',
    onSortChange,
    onFolderClick,
    onDocumentClick,
    onFolderRename,
    onFolderDelete,
    onFolderMove,
    onDocumentRename,
    onDocumentDownload,
    onDocumentDelete,
    onDocumentMove,
    canWrite = true,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedItemType, setSelectedItemType] = useState(null); // 'folder' or 'document'

    const handleMenuOpen = (event, item, type) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedItem(item);
        setSelectedItemType(type);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedItem(null);
        setSelectedItemType(null);
    };

    const handleRename = () => {
        if (selectedItem && selectedItemType === 'folder' && onFolderRename) {
            onFolderRename(selectedItem);
        } else if (selectedItem && selectedItemType === 'document' && onDocumentRename) {
            onDocumentRename(selectedItem);
        }
        handleMenuClose();
    };

    const handleDelete = () => {
        if (selectedItem) {
            if (selectedItemType === 'folder' && onFolderDelete) {
                onFolderDelete(selectedItem);
            } else if (selectedItemType === 'document' && onDocumentDelete) {
                onDocumentDelete(selectedItem);
            }
        }
        handleMenuClose();
    };

    const handleMove = () => {
        if (selectedItem) {
            if (selectedItemType === 'folder' && onFolderMove) {
                onFolderMove(selectedItem);
            } else if (selectedItemType === 'document' && onDocumentMove) {
                onDocumentMove(selectedItem);
            }
        }
        handleMenuClose();
    };

    const handleDownload = () => {
        if (selectedItem && selectedItemType === 'document' && onDocumentDownload) {
            onDocumentDownload(selectedItem);
        }
        handleMenuClose();
    };

    if (folders.length === 0 && documents.length === 0) {
        return null;
    }

    // Helper to render sort indicator icon
    const renderSortIcon = (column) => {
        const isActive = sortBy === column;
        const icon =
            sortBy === column && sortOrder === 'asc' ? (
                <ArrowUpwardIcon fontSize="small" sx={{ ml: 0.5 }} />
            ) : (
                <ArrowDownwardIcon fontSize="small" sx={{ ml: 0.5 }} />
            );

        return (
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    ml: 0.5,
                    opacity: isActive ? 1 : 0.3,
                    transition: 'opacity 0.2s ease',
                }}
            >
                {icon}
            </Box>
        );
    };

    // Mobile/Tablet View - Grid2 Card Layout
    if (isMobile) {
        return (
            <>
                <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
                    {/* Render folders first */}
                    {folders.map((folder) => (
                        <Grid size={{ xs: 12 }} key={`folder-${folder.folderId}`}>
                            <Card
                                elevation={0}
                                sx={{
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    '&:hover': {
                                        boxShadow: theme.shadows[2],
                                        borderColor: theme.palette.primary.main,
                                    },
                                }}
                                onClick={() => onFolderClick && onFolderClick(folder.folderId)}
                            >
                                <CardContent
                                    sx={{
                                        p: { xs: 1.5, sm: 2 },
                                        '&:last-child': { pb: { xs: 1.5, sm: 2 } },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 2,
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            <FolderIcon
                                                sx={{
                                                    fontSize: 32,
                                                    color: theme.palette.primary.main,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontWeight: 600,
                                                        mb: 0.5,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {folder.folderName}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    display="block"
                                                >
                                                    {folder.documentCount || 0} document
                                                    {folder.documentCount !== 1 ? 's' : ''},{' '}
                                                    {folder.subfolderCount || 0} folder
                                                    {folder.subfolderCount !== 1 ? 's' : ''}
                                                </Typography>
                                                {folder.createdAt && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Created {formatDate(folder.createdAt)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        {canWrite && (
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleMenuOpen(e, folder, 'folder')}
                                                sx={{ ml: 1, flexShrink: 0 }}
                                            >
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}

                    {/* Render documents */}
                    {documents.map((document) => (
                        <Grid size={{ xs: 12 }} key={`document-${document.documentId}`}>
                            <Card
                                elevation={0}
                                sx={{
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    '&:hover': {
                                        boxShadow: theme.shadows[2],
                                        borderColor: theme.palette.primary.main,
                                    },
                                }}
                                onClick={() => onDocumentClick && onDocumentClick(document)}
                            >
                                <CardContent
                                    sx={{
                                        p: { xs: 1.5, sm: 2 },
                                        '&:last-child': { pb: { xs: 1.5, sm: 2 } },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 2,
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            <Box sx={{ flexShrink: 0 }}>
                                                {getFileIcon(document.fileExtension, 'large')}
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontWeight: 600,
                                                        mb: 0.5,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                    title={document.originalFilename}
                                                >
                                                    {document.originalFilename}
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 1,
                                                        mb: 0.5,
                                                        flexWrap: 'wrap',
                                                    }}
                                                >
                                                    {document.fileExtension && (
                                                        <Chip
                                                            label={document.fileExtension.toUpperCase()}
                                                            size="small"
                                                            sx={{ fontSize: '0.7rem', height: 20 }}
                                                        />
                                                    )}
                                                    {document.fileSizeBytes && (
                                                        <Chip
                                                            label={formatFileSize(
                                                                document.fileSizeBytes
                                                            )}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ fontSize: '0.7rem', height: 20 }}
                                                        />
                                                    )}
                                                </Box>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    display="block"
                                                >
                                                    {document.uploadedByName || 'Unknown'}
                                                </Typography>
                                                {document.uploadedAt && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {formatDate(document.uploadedAt)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleMenuOpen(e, document, 'document')}
                                            sx={{ ml: 1, flexShrink: 0 }}
                                        >
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Action Menu */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    {canWrite && (
                        <MenuItem onClick={handleRename}>
                            <ListItemIcon>
                                <EditIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Rename</ListItemText>
                        </MenuItem>
                    )}

                    {selectedItemType === 'document' && (
                        <MenuItem onClick={handleDownload}>
                            <ListItemIcon>
                                <DownloadIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Download</ListItemText>
                        </MenuItem>
                    )}

                    {canWrite && (
                        <MenuItem onClick={handleMove}>
                            <ListItemIcon>
                                <DriveFileMoveIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Move</ListItemText>
                        </MenuItem>
                    )}

                    {canWrite && (
                        <MenuItem onClick={handleDelete} sx={{ color: theme.palette.error.main }}>
                            <ListItemIcon>
                                <DeleteIcon fontSize="small" color="error" />
                            </ListItemIcon>
                            <ListItemText>Delete</ListItemText>
                        </MenuItem>
                    )}
                </Menu>
            </>
        );
    }

    // Desktop View - Single Unified Table
    return (
        <>
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow
                            sx={{
                                backgroundColor: theme.palette.grey[50],
                            }}
                        >
                            <TableCell
                                sx={{
                                    fontWeight: 600,
                                    width: '40%',
                                    py: 1,
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    transition: 'background-color 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: theme.palette.action.hover,
                                    },
                                    '&:hover .sort-icon': {
                                        opacity: 1,
                                    },
                                }}
                                onClick={() => onSortChange && onSortChange('name')}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    Name
                                    <Box className="sort-icon">{renderSortIcon('name')}</Box>
                                </Box>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 1 }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 1 }}>Size</TableCell>
                            <TableCell sx={{ fontWeight: 600, py: 1 }}>Uploaded By</TableCell>
                            <TableCell
                                sx={{
                                    fontWeight: 600,
                                    py: 1,
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    transition: 'background-color 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: theme.palette.action.hover,
                                    },
                                    '&:hover .sort-icon': {
                                        opacity: 1,
                                    },
                                }}
                                onClick={() => onSortChange && onSortChange('date')}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    Date
                                    <Box className="sort-icon">{renderSortIcon('date')}</Box>
                                </Box>
                            </TableCell>
                            <TableCell sx={{ width: 50, py: 1 }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {/* Render folders first */}
                        {folders.map((folder) => (
                            <TableRow
                                key={`folder-${folder.folderId}`}
                                onClick={() => onFolderClick && onFolderClick(folder.folderId)}
                                sx={{
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: theme.palette.action.hover,
                                    },
                                }}
                            >
                                <TableCell sx={{ py: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <FolderIcon
                                            sx={{
                                                fontSize: 24,
                                                color: theme.palette.primary.main,
                                            }}
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {folder.folderName}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Folder
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {folder.documentCount || 0} document
                                        {folder.documentCount !== 1 ? 's' : ''},{' '}
                                        {folder.subfolderCount || 0} folder
                                        {folder.subfolderCount !== 1 ? 's' : ''}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        —
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                    {folder.createdAt && (
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDate(folder.createdAt)}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                    {canWrite && (
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleMenuOpen(e, folder, 'folder')}
                                        >
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}

                        {/* Render documents */}
                        {documents.map((document) => (
                            <TableRow
                                key={`document-${document.documentId}`}
                                onClick={() => onDocumentClick && onDocumentClick(document)}
                                sx={{
                                    cursor: 'pointer',
                                    transition: 'background-color 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: theme.palette.action.hover,
                                    },
                                }}
                            >
                                <TableCell sx={{ py: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        {getFileIcon(document.fileExtension, 'small')}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                            title={document.originalFilename}
                                        >
                                            {document.originalFilename}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                    {document.fileExtension && (
                                        <Chip
                                            label={document.fileExtension.toUpperCase()}
                                            size="small"
                                            sx={{ fontSize: '0.7rem', height: 20 }}
                                        />
                                    )}
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                    {document.fileSizeBytes && (
                                        <Typography variant="body2" color="text.secondary">
                                            {formatFileSize(document.fileSizeBytes)}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {document.uploadedByName || 'Unknown'}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                    {document.uploadedAt && (
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDate(document.uploadedAt)}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell sx={{ py: 1 }}>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleMenuOpen(e, document, 'document')}
                                    >
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Action Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                {canWrite && (
                    <MenuItem onClick={handleRename}>
                        <ListItemIcon>
                            <EditIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Rename</ListItemText>
                    </MenuItem>
                )}

                {selectedItemType === 'document' && (
                    <MenuItem onClick={handleDownload}>
                        <ListItemIcon>
                            <DownloadIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Download</ListItemText>
                    </MenuItem>
                )}

                {canWrite && (
                    <MenuItem onClick={handleMove}>
                        <ListItemIcon>
                            <DriveFileMoveIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Move</ListItemText>
                    </MenuItem>
                )}

                {canWrite && (
                    <MenuItem onClick={handleDelete} sx={{ color: theme.palette.error.main }}>
                        <ListItemIcon>
                            <DeleteIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

UnifiedList.propTypes = {
    folders: PropTypes.arrayOf(
        PropTypes.shape({
            folderId: PropTypes.string.isRequired,
            folderName: PropTypes.string.isRequired,
            documentCount: PropTypes.number,
            subfolderCount: PropTypes.number,
            createdAt: PropTypes.string,
        })
    ),
    documents: PropTypes.arrayOf(
        PropTypes.shape({
            documentId: PropTypes.string.isRequired,
            originalFilename: PropTypes.string.isRequired,
            fileExtension: PropTypes.string,
            fileSizeBytes: PropTypes.number,
            uploadedByName: PropTypes.string,
            uploadedAt: PropTypes.string,
        })
    ),
    sortBy: PropTypes.oneOf(['name', 'date']),
    sortOrder: PropTypes.oneOf(['asc', 'desc']),
    onSortChange: PropTypes.func,
    onFolderClick: PropTypes.func,
    onDocumentClick: PropTypes.func,
    onFolderRename: PropTypes.func,
    onFolderDelete: PropTypes.func,
    onFolderMove: PropTypes.func,
    onDocumentRename: PropTypes.func,
    onDocumentDownload: PropTypes.func,
    onDocumentDelete: PropTypes.func,
    onDocumentMove: PropTypes.func,
    canWrite: PropTypes.bool,
};

UnifiedList.defaultProps = {
    folders: [],
    documents: [],
    sortBy: 'name',
    sortOrder: 'asc',
    onSortChange: null,
    onFolderClick: null,
    onDocumentClick: null,
    onFolderRename: null,
    onFolderDelete: null,
    onFolderMove: null,
    onDocumentRename: null,
    onDocumentDownload: null,
    onDocumentDelete: null,
    onDocumentMove: null,
    canWrite: true,
};

export default UnifiedList;
